/**
 * Per-device board overview state kept in localStorage (cards 7.2/7.3):
 * when each board was last opened HERE, and the overview's sort and card/row
 * view. Deliberately local and never synced (board decision 11.10) — "last
 * opened" on a laptop says nothing about a phone, and none of it may enter a
 * board record.
 *
 * Every access is guarded: private windows, blocked storage or a corrupt
 * value fall back to the defaults rather than breaking the Boards home.
 */
import { SORT_OPENED, isSortKey } from './boardsOverview';

export const LAST_OPENED_KEY = 'apiboo.boards.lastOpened';
export const OVERVIEW_PREFS_KEY = 'apiboo.boards.overviewPrefs';
/** Boards kept in the map; the oldest beyond this are dropped on write. */
export const LAST_OPENED_MAX = 200;

export const VIEW_CARDS = 'cards';
export const VIEW_ROWS = 'rows';
export const DEFAULT_OVERVIEW_PREFS = Object.freeze({ sort: SORT_OPENED, view: VIEW_CARDS });

function readJson(key) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJson(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the in-memory value still applies this session.
  }
}

/** { [boardId]: epoch ms }, numbers only. */
export function loadLastOpened() {
  const raw = readJson(LAST_OPENED_KEY);
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  for (const [id, at] of Object.entries(raw)) {
    if (typeof at === 'number' && Number.isFinite(at) && at > 0) out[id] = at;
  }
  return out;
}

/** The map with `boardId` stamped `at`, capped to the newest LAST_OPENED_MAX. */
export function withOpened(map, boardId, at = Date.now(), max = LAST_OPENED_MAX) {
  if (boardId == null || boardId === '') return map || {};
  const next = { ...(map || {}), [String(boardId)]: at };
  const entries = Object.entries(next);
  if (entries.length <= max) return next;
  return Object.fromEntries(entries.sort((a, b) => b[1] - a[1]).slice(0, max));
}

export function saveLastOpened(map) {
  writeJson(LAST_OPENED_KEY, map || {});
}

export function loadOverviewPrefs() {
  const raw = readJson(OVERVIEW_PREFS_KEY) || {};
  return {
    sort: isSortKey(raw.sort) ? raw.sort : DEFAULT_OVERVIEW_PREFS.sort,
    view: raw.view === VIEW_ROWS ? VIEW_ROWS : VIEW_CARDS,
  };
}

export function saveOverviewPrefs(prefs) {
  writeJson(OVERVIEW_PREFS_KEY, { sort: prefs?.sort, view: prefs?.view });
}
